/* Dearingold product configurator: <dg-configurator>.
   Vanilla JS, no dependencies, no build step. Reads two JSON blobs rendered by Liquid:
     #dg-rules            global rules (snippets/dg-rules.liquid)
     [data-dg-product]    this product: config metafield, variants (+ route map), occasion stories
   Adds to cart through Shopify's standard Ajax Cart API (window.DG.cart.add), so it works unchanged
   on a real store. Copy shown here is product-truth copy: see docs/GUARDRAILS.md before editing. */
(() => {
  'use strict';

  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const rid = (prefix) => {
    let r = '';
    if (window.crypto && crypto.getRandomValues) {
      const a = new Uint8Array(10);
      crypto.getRandomValues(a);
      r = Array.from(a, (b) => b.toString(16).padStart(2, '0')).join('');
    } else r = Math.random().toString(36).slice(2) + Date.now().toString(36);
    return `${prefix}_${r}`;
  };
  const money = (c) => (window.DG && DG.money ? DG.money(c) : '$' + (Number(c || 0) / 100).toFixed(2).replace(/\.00$/, ''));
  const reducedMotion = () => window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
  const parseISO = (s) => { const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || ''); return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null; };
  const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const longDate = (d) => d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  const shortDate = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const addBusinessDays = (from, n) => {
    const d = new Date(from.getFullYear(), from.getMonth(), from.getDate());
    let left = Math.max(0, Math.round(n));
    while (left > 0) { d.setDate(d.getDate() + 1); const w = d.getDay(); if (w !== 0 && w !== 6) left--; }
    return d;
  };

  // format code -> dg-art finish is rendered server-side (one hidden dg-art per format, [data-art-for]).
  // rules.fields key -> public line-item property label (human-readable, shown to the customer)
  const FIELD_PROP = { names: 'Names', date: 'Date', place: 'Place', 'family-name': 'Family name', years: 'Years', 'pet-name': 'Name', 'first-name': 'First name', caption: 'Caption', arrangement: 'Art notes', 'names-optional': 'Names', 'date-optional': 'Date' };
  const REMEMBRANCE_STORY_IDS = ['remembering', 'a-pet'];
  const COMPOSITE_T = ['composite', 'restoration-composite'];
  const RESTORATION_T = ['restoration', 'restoration-composite', 'architecture-restoration'];
  const FRAME_SWATCH = { black: '#1a1917', natural: '#B69369', white: '#F4F1EA', signature: '#2c261b', 'antique silver': '#aaa9a4', brown: '#674936', 'antique gold': '#aa8750', 'dark grey': '#555655', 'light grey': '#c9c7c1' };

  class DGConfigurator extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      try { this.setup(); } catch (err) { console.error('[dg-configurator]', err); }
    }

    $(sel) { return this.querySelector(sel); }

    /** Replace innerHTML only when the markup really changed (ignoring checked state), so keyboard
        focus inside a radio group survives re-renders. Checked state is synced in place. */
    paint(el, html) {
      if (!el) return;
      const sig = html.replace(/ checked(?=[\s>])/g, '');
      if (el._sig === sig) {
        const tpl = document.createElement('template');
        tpl.innerHTML = html;
        const want = new Set(Array.from(tpl.content.querySelectorAll('input[checked]'), (i) => `${i.name}|${i.value}`));
        el.querySelectorAll('input[type="radio"], input[type="checkbox"]').forEach((i) => { i.checked = want.has(`${i.name}|${i.value}`); });
        return;
      }
      el._sig = sig;
      el.innerHTML = html;
    }
    $$(sel) { return Array.from(this.querySelectorAll(sel)); }

    setup() {
      const root = this.closest('.shopify-section') || document;
      this.rules = JSON.parse((root.querySelector('#dg-rules') || document.getElementById('dg-rules')).textContent);
      this.data = JSON.parse((root.querySelector('[data-dg-product]') || document.querySelector('[data-dg-product]')).textContent);
      this.cfg = this.data.config || {};
      this.form = this.$('form');
      const localPreview = ['localhost', '127.0.0.1'].includes(location.hostname) && this.rules.prototypeMode;
      const passwordPreview = this.rules.prototypeMode && this.rules.previewCheckout;
      this.variants = (this.data.variants || []).map((v) => ({ ...v, available: v.available && (localPreview || passwordPreview || v.verification === 'approved'), size: v.options[1], frame: v.options[2] }));
      if (!this.variants.some((v) => v.available)) {
        this.form.hidden = true;
        this.$('[data-price]').textContent = 'Not yet available';
        this.$('[data-availability-note]').hidden = false;
        this.$('[data-bar]').style.display = 'none';
        const after = this.closest('.pdp')?.querySelector('[data-after]');
        if (after) after.style.display = 'none';
        return;
      }

      // Occasion stories that point at this product (collections without matching stories are kept
      // only as a fallback source for the occasion eyebrow).
      const cols = (this.data.collections || []).filter(Boolean);
      this.primaryOccasion = (cols.find((c) => c.avatar === this.cfg.avatar) || {}).title || '';
      this.stories = [];
      cols.forEach((c) => c.stories.forEach((s) => this.stories.push({ ...s, key: `${c.handle}:${s.id}`, occasion: c.title, collection: c.handle })));

      const sel = this.variants.find((v) => v.id === this.data.selected) || this.variants.find((v) => v.available) || this.variants[0];
      this.s = {
        story: null, storyOther: '',
        treatment: null,
        format: sel ? sel.format : (this.cfg.formats || [])[0],
        size: sel ? sel.size : null,
        frame: sel ? sel.frame : null,
        photos: [], inspiration: null, styleDirection: '',
        values: {},
        proofOptIn: false, priority: false, neededBy: '',
        gifts: new Set(), giftNote: '',
        rights: false,
      };
      this.newIds();

      // ?story=<id> preselects the story (optionally disambiguated with ?occasion=<collection handle>)
      const q = new URLSearchParams(location.search);
      const sid = q.get('story');
      this.storyFromLink = false;
      if (sid) {
        const occ = q.get('occasion');
        const hit = this.stories.find((s) => s.id === sid && (!occ || s.collection === occ)) || this.stories.find((s) => s.id === sid);
        if (hit) { this.s.story = hit.key; this.storyFromLink = true; }
      }

      this.renderStatic();
      this.renderTreatments();
      this.renderFormats();
      this.renderFields();
      this.bind();
      this.update();
    }

    newIds() {
      // With remote uploads the bundle (the photos) is shared by every piece made from this page;
      // only the artwork request id is new per cart line.
      this.ids = { bundle: this.remote ? this.remote.bundleId : rid('ub'), request: rid('ar') };
    }

    // ---------------------------------------------------------------- uploads (dearingold-uploads Worker)
    get uploadEndpoint() { return String(this.rules.uploadEndpoint || '').replace(/\/+$/, ''); }

    async ensureBundle() {
      if (this.remote) return this.remote;
      if (!this._bundleP) {
        this._bundleP = fetch(`${this.uploadEndpoint}/v1/bundles`, { method: 'POST' })
          .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`bundle ${r.status}`))))
          .then((b) => { this.remote = b; this.ids.bundle = b.bundleId; return b; })
          .catch((e) => { this._bundleP = null; throw e; });
      }
      return this._bundleP;
    }

    async uploadPhoto(entry) {
      entry.upload = 'uploading'; entry.progress = 0; this.update();
      try {
        const b = await this.ensureBundle();
        const res = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          const uploadName = entry.kind === 'inspiration' ? `inspiration-${entry.name}` : entry.name;
          xhr.open('PUT', `${this.uploadEndpoint}/v1/bundles/${b.bundleId}/files?name=${encodeURIComponent(uploadName)}`);
          xhr.setRequestHeader('Authorization', `Bearer ${b.token}`);
          xhr.setRequestHeader('Content-Type', entry.file.type || 'application/octet-stream');
          xhr.upload.onprogress = (e) => { if (e.lengthComputable) { entry.progress = Math.round((e.loaded / e.total) * 100); this.renderUploadProgress(entry); } };
          xhr.onload = () => { let body = {}; try { body = JSON.parse(xhr.responseText); } catch (_) {} xhr.status < 300 ? resolve(body) : reject(Object.assign(new Error(body.error || `upload ${xhr.status}`), { status: xhr.status })); };
          xhr.onerror = () => reject(new Error('network'));
          xhr.send(entry.file);
        });
        entry.remote = res.name; entry.upload = 'done';
        if (entry.kind === 'inspiration' && this.s.inspiration !== entry) this.deleteRemoteFile(entry);
      } catch (err) {
        console.warn('[dg-configurator] upload failed', err);
        entry.upload = 'failed';
        entry.uploadError = err.status === 415 ? 'This file type can’t be used. Please send a JPG, PNG, WEBP or HEIC.'
          : err.status === 413 ? 'This file is too large.'
          : err.status === 409 ? 'This order already has the maximum number of images.'
          : 'Upload didn’t finish. Check your connection and retry.';
      }
      this.update();
    }

    renderUploadProgress(entry) {
      const el = this.querySelector(`[data-progress="${entry.id}"]`);
      if (el) { el.style.setProperty('--p', `${entry.progress}%`); el.setAttribute('aria-valuenow', String(entry.progress)); }
    }

    deleteRemoteFile(entry) {
      if (!entry?.remote || !this.remote || this.remote.attached) return;
      fetch(`${this.uploadEndpoint}/v1/bundles/${this.remote.bundleId}/files/${entry.remote}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${this.remote.token}` },
      }).catch(() => {});
    }

    // ---------------------------------------------------------------- derived state
    get story() { return this.stories.find((s) => s.key === this.s.story) || null; }
    get remembrance() {
      const st = this.story;
      return this.data.handle === 'still-part-of-home' || !!st && (st.collection === 'remembrance' || REMEMBRANCE_STORY_IDS.includes(st.id));
    }
    get variant() {
      return this.variants.find((v) => v.format === this.s.format && v.size === this.s.size && v.frame === this.s.frame && v.available) || null;
    }
    get quantity() { return this.s.gifts.has('duplicate') ? 2 : 1; }
    /** Priced add-on variant. Prefers #dg-rules, falls back to the section's own lookup. */
    addon(handle) {
      const r = (this.rules.addons || {})[handle];
      if (r && r.variantId) return r;
      const d = (this.data.addons || {})[handle];
      return d && d.variantId ? d : null;
    }
    get priorityAvailable() { return (this.cfg.gifts || []).includes('priority-proof') && !!this.addon('priority-proof'); }

    allowedTreatments() {
      return (this.cfg.treatments || []).filter((t) => {
        const r = this.rules.treatments[t];
        return r && !(this.remembrance && r.livingOnly);
      });
    }
    formatsWithVariants() {
      return (this.cfg.formats || []).filter((f) => this.rules.formats[f] && this.variants.some((v) => v.format === f && v.available));
    }
    giftOptions() {
      return (this.cfg.gifts || []).filter((g) => {
        if (g === 'priority-proof') return false; // offered in the proof step
        const r = this.rules.gifts[g];
        if (!r) return false;
        if (r.formats && !r.formats.includes(this.s.format)) return false;
        if (r.addon && !this.addon(r.addon)) return false;
        return true;
      });
    }
    isMultiPet() {
      const st = this.story;
      return (st && st.id === 'multi-pet') || (this.cfg.avatar === 'pets' && this.s.photos.length > 1);
    }

    /** Every artwork receives customer approval before production. */
    proofFor(tcode) {
      const t = this.rules.treatments[tcode] || {};
      const reasons = [t.proof === 'mandatory' ? `${t.label} includes a proof.` : 'A customer proof is included with every piece.'];
      return { level: 'customer', reasons, forced: true };
    }
    get proof() { return this.proofFor(this.s.treatment); }

    estimate() {
      const L = this.rules.lead || {};
      const p = this.proof;
      const v = this.variant;
      const route = (v && v.route) || (this.rules.formats[this.s.format] || {}).route;
      const artBase = p.level === 'customer' ? L.proof : L.qa;
      const art = (prio) => Math.max(1, artBase - (prio ? L.prioritySaving || 0 : 0));
      const make = route === 'MOON-INHOUSE' ? L.moonshape : L.provider;
      const today = new Date();
      const win = (prio) => ({ from: addBusinessDays(today, art(prio) + make + L.transitMin), to: addBusinessDays(today, art(prio) + make + L.transitMax) });
      const w = win(this.s.priority);
      const needed = parseISO(this.s.neededBy);
      let status = null;
      let cls = 'standard';
      if (needed) {
        const withPrio = this.priorityAvailable && !this.s.priority ? win(true) : null;
        if (w.to <= needed) status = { key: 'ok', text: 'On track' };
        else if (w.from <= needed) {
          cls = 'date-sensitive';
          status = withPrio ? { key: 'tight', text: 'Tight: consider Priority proof' } : { key: 'tight', text: 'Tight: it may arrive after this date' };
        } else {
          cls = 'not-available';
          status = { key: 'no', text: "We can't promise this date" };
          if (withPrio && withPrio.from <= needed) status.hint = 'Priority proof brings the art stage forward, which could help. Manufacturing and shipping times stay the same.';
        }
      }
      return { ...w, art: art(this.s.priority), make, route, transitMin: L.transitMin, transitMax: L.transitMax, status, cls, needed };
    }

    // ---------------------------------------------------------------- rendering
    renderStatic() {
      const ph = this.cfg.photos || { min: 1, max: 1 };
      const P = this.rules.photo || {};
      const input = this.$('[data-file-input]');
      if (ph.max > 1) input.multiple = true;
      const n = ph.min === ph.max ? `${ph.min}` : `${ph.min} to ${ph.max}`;
      const composite = (this.cfg.treatments || []).some((t) => COMPOSITE_T.includes(t));
      this.$('[data-photos-hint]').textContent =
        `Add ${n} photo${ph.max > 1 ? 's' : ''}. JPG, PNG or HEIC, up to ${P.maxMb} MB each. Originals work best, not screenshots.` +
        (composite ? ' Please send one photo per person, and tell us roughly how tall each person is.' : '');
      this.$('[data-rights-label]').textContent = this.rules.attestations.rights.label;
      this.$('[data-consent]').textContent = this.rules.attestations.consent.label;
    }

    renderTreatments() {
      const allowed = this.allowedTreatments();
      if (!allowed.includes(this.s.treatment)) this.s.treatment = allowed[0] || null;
      this.paint(this.$('[data-treatments]'), allowed.map((code) => {
        const t = this.rules.treatments[code];
        return this.card({ type: 'radio', name: 'dg-treatment', value: code, checked: this.s.treatment === code, title: t.label, body: t.line, image: this.cfg.avatar === 'pets' ? (t.petImage || t.image) : t.image, imageAlt: `Illustrative example of ${t.label}`, extraClass: 'pdp-card--visual' });
      }).join(''));
      this.$('[data-step="treatment"]').classList.toggle('pdp-step--single', allowed.length === 1);
    }

    proofBadge(code) {
      const p = this.proofFor(code);
      const t = this.rules.treatments[code] || {};
      if (p.level === 'customer') return { text: 'Proof included', tone: 'gold' };
      return { text: 'Artist-reviewed', tone: '' };
    }

    renderFormats() {
      const fmts = this.formatsWithVariants();
      if (!fmts.includes(this.s.format)) this.s.format = fmts[0];
      this.paint(this.$('[data-formats]'), fmts.map((code) => {
        const f = this.rules.formats[code];
        const prices = this.variants.filter((v) => v.format === code && v.available).map((v) => v.price);
        return this.card({
          type: 'radio', name: 'dg-format', value: code, checked: this.s.format === code,
          title: f.label, body: f.truth, tag: f.short, price: prices.length ? `From ${money(Math.min(...prices))}` : '',
          image: f.image, imageAlt: `${f.made === 'studio' ? 'Studio' : 'Prodigi'} material example: ${f.label}`, extraClass: `pdp-card--visual pdp-card--fmt pdp-card--${code}`,
        });
      }).join(''));
    }

    renderSizeFrame() {
      const fv = this.variants.filter((v) => v.format === this.s.format);
      const sizes = [...new Set(fv.map((v) => v.size))];
      const frames = [...new Set(fv.map((v) => v.frame))];
      const sizeOk = (sz) => fv.some((v) => v.size === sz && v.available);
      if (!sizeOk(this.s.size)) this.s.size = sizes.find(sizeOk) || sizes[0];
      const frameOk = (fr) => fv.some((v) => v.size === this.s.size && v.frame === fr && v.available);
      if (!frameOk(this.s.frame)) this.s.frame = frames.find(frameOk) || frames[0];

      this.paint(this.$('[data-sizes]'), sizes.map((sz) => {
        const prices = fv.filter((v) => v.size === sz && v.available).map((v) => v.price);
        const price = prices.length ? Math.min(...prices) : null;
        const ok = sizeOk(sz);
        return `<label class="pdp-chip${ok ? '' : ' is-disabled'}">
          <input class="pdp-chip__input" type="radio" name="dg-size" value="${esc(sz)}"${this.s.size === sz ? ' checked' : ''}${ok ? '' : ' disabled'}>
          <span class="pdp-chip__body"><span class="pdp-chip__main">${esc(sz.replace('×', ' × '))}</span><span class="pdp-chip__sub">${ok ? money(price) : 'Unavailable'}</span></span>
        </label>`;
      }).join(''));

      const classic = ['framed-print', 'gold-print', 'metallic-foil'].includes(this.s.format);
      const shownFrames = frames;
      this.$('[data-frame-legend]').textContent = classic ? 'Frame' : 'Presentation';
      this.$('[data-frame-note]').hidden = !classic;
      this.paint(this.$('[data-frames]'), shownFrames.map((fr) => {
        const ok = frameOk(fr);
        const sw = FRAME_SWATCH[String(fr).toLowerCase()] || '#999';
        const photo = (this.rules.frameImages || {})[fr];
        return `<label class="pdp-chip pdp-chip--frame${ok ? '' : ' is-disabled'}">
          <input class="pdp-chip__input" type="radio" name="dg-frame" value="${esc(fr)}"${this.s.frame === fr ? ' checked' : ''}${ok ? '' : ' disabled'}>
          <span class="pdp-chip__body">${photo ? `<img class="pdp-swatch" src="${esc(photo)}" alt="">` : `<span class="pdp-swatch" style="--sw:${sw}" aria-hidden="true"></span>`}<span class="pdp-chip__main">${esc(fr)}</span>${ok ? '' : '<span class="pdp-chip__sub">Not in this size</span>'}</span>
        </label>`;
      }).join(''));
      this.$('[data-step="size"]').classList.toggle('pdp-step--single-frame', !classic && frames.length === 1);
    }

    fieldRequired(key, f) {
      return f.type !== 'checkbox' && !f.private && !/\(optional\)/i.test(f.label || '');
    }

    renderFields() {
      const keys = (this.cfg.fields || []).filter((k) => this.rules.fields[k]);
      const today = toISO(new Date());
      const html = keys.map((k) => {
        const f = this.rules.fields[k];
        const id = `dg-f-${k}`;
        const req = this.fieldRequired(k, f);
        const errId = `dg-err-${k}`;
        const label = esc(f.label);
        const describedBy = [f.private ? `${id}-hint` : '', errId].filter(Boolean).join(' ');
        if (f.type === 'checkbox') {
          return `<div class="pdp-field pdp-field--check">
            <label class="pdp-check"><input type="checkbox" id="${id}" data-field="${k}"><span>${label}${f.forcesProof ? '<span class="pdp-field__note">We\'ll show you a proof before anything is made.</span>' : ''}</span></label>
          </div>`;
        }
        let control;
        if (f.type === 'textarea') control = `<textarea class="textarea" id="${id}" data-field="${k}" rows="4"${f.max ? ` maxlength="${f.max}"` : ''} placeholder="${esc(f.placeholder || '')}"${req ? ' aria-required="true"' : ''} aria-describedby="${describedBy}"></textarea>`;
        else if (f.type === 'date') control = `<input class="input" type="date" id="${id}" data-field="${k}" max="${today}"${req ? ' aria-required="true"' : ''} aria-describedby="${describedBy}">`;
        else control = `<input class="input" type="text" id="${id}" data-field="${k}"${f.max ? ` maxlength="${f.max}"` : ''} placeholder="${esc(f.placeholder || '')}" autocomplete="${f.private ? 'street-address' : 'off'}"${req ? ' aria-required="true"' : ''} aria-describedby="${describedBy}">`;
        return `<div class="field pdp-field${f.type === 'textarea' ? ' pdp-field--wide' : ''}">
          <label for="${id}">${label}${req ? ' <span class="pdp-req" aria-hidden="true">*</span>' : ''}</label>
          ${control}
          ${f.private ? `<p class="hint" id="${id}-hint">${esc(f.placeholder || 'Private. Never printed.')}</p>` : ''}
          ${f.max && f.type !== 'date' ? `<span class="pdp-count small muted" data-count-for="${k}" aria-hidden="true">0 / ${f.max}</span>` : ''}
          <p class="pdp-error" id="${errId}" data-error-for="${k}"></p>
        </div>`;
      });
      html.push(`<div class="field pdp-field pdp-field--wide">
        <label for="dg-f-notes">Anything else your artist should know? <span class="muted">(optional)</span></label>
        <textarea class="textarea" id="dg-f-notes" data-field="notes" rows="3" maxlength="400" placeholder="Colors to keep, a detail that matters, who is who."></textarea>
      </div>`);
      this.$('[data-fields]').innerHTML = html.join('');
    }

    renderProof() {
      const p = this.proof;
      const R = this.rules.proof;
      const lvl = R[p.level];
      this.$('[data-proof]').innerHTML = `
        <div class="pdp-proof__level pdp-proof__level--${p.level}">
          <span class="pdp-proof__label">${esc(lvl.label)}</span>
          <p class="pdp-proof__line">${esc(lvl.line)}</p>
          ${p.reasons.length ? `<p class="pdp-proof__why small">${esc(p.reasons[0])}</p>` : ''}
        </div>`;
      const opts = [];
      if (this.priorityAvailable) {
        const a = this.addon('priority-proof');
        opts.push(`<label class="pdp-check">
          <input type="checkbox" name="dg-priority" value="1" data-priority${this.s.priority ? ' checked' : ''}>
          <span>${esc(R.priority.label)} <span class="pdp-check__price">+${money(a.price)}</span><span class="pdp-field__note">${esc(R.priority.line)}</span></span>
        </label>`);
      }
      this.paint(this.$('[data-proof-opts]'), opts.join(''));
    }

    renderEstimate() {
      const e = this.estimate();
      const art = this.proof.level === 'customer' ? 'art review and your proof' : 'art review';
      const made = e.route === 'MOON-INHOUSE' ? 'made by hand in our studio' : 'professionally printed and finished';
      let statusHtml = '';
      if (e.status) {
        statusHtml = `<p class="pdp-estimate__status pdp-estimate__status--${e.status.key}"><span class="pdp-dot" aria-hidden="true"></span>${esc(e.status.text)}${e.needed ? ` <span class="muted">for ${esc(shortDate(e.needed))}</span>` : ''}</p>`;
        if (e.status.hint) statusHtml += `<p class="small muted">${esc(e.status.hint)}</p>`;
        if (e.status.key === 'no') statusHtml += '<p class="small muted">You can still order. We\'ll tell you honestly if we can find a way.</p>';
      }
      this.$('[data-estimate]').innerHTML = `
        <p class="pdp-estimate__range">Arrives approx. <strong>${esc(shortDate(e.from))} – ${esc(shortDate(e.to))}</strong></p>
        <p class="pdp-estimate__math small muted">${e.art} business days for ${art}, ${e.make} to be ${made}, then ${e.transitMin}–${e.transitMax} in transit.${this.s.priority ? ' Priority proof shortens the art stage only.' : ''}</p>
        ${statusHtml}`;
      this._estimate = e;
    }

    renderGifts() {
      const opts = this.giftOptions();
      for (const g of [...this.s.gifts]) if (!opts.includes(g)) this.s.gifts.delete(g);
      const step = this.$('[data-step="gift"]');
      step.hidden = !opts.length;
      const v = this.variant;
      this.paint(this.$('[data-gifts]'), opts.map((g) => {
        const r = this.rules.gifts[g];
        const price = r.addon ? this.addon(r.addon).price : g === 'duplicate' && v ? v.price : 0;
        return this.card({ type: 'checkbox', name: 'dg-gift', value: g, checked: this.s.gifts.has(g), title: r.label, body: r.line, price: price ? `+${money(price)}` : '' });
      }).join(''));
      this.$('[data-gift-note]').hidden = !this.s.gifts.has('handwritten-note');
    }

    card({ type, name, value, checked, title, body, meta, badge, tag, tier, price, image, imageAlt, extraClass }) {
      return `<label class="pdp-card ${extraClass || ''}">
        <input class="pdp-card__input" type="${type}" name="${esc(name)}" value="${esc(value)}"${checked ? ' checked' : ''}>
        <span class="pdp-card__body">
          ${image ? `<span class="pdp-card__image"><img src="${esc(image)}" alt="${esc(imageAlt || '')}" loading="lazy"></span>` : ''}
          <span class="pdp-card__top">
            ${tier ? `<span class="pdp-tier pdp-tier--${esc(tier)}" aria-hidden="true"></span>` : ''}
            <span class="pdp-card__title">${esc(title)}</span>
            ${price ? `<span class="pdp-card__price">${esc(price)}</span>` : ''}
            ${tag ? `<span class="tag${tier && tier !== 'framed-print' ? ' tag--gold' : ''}">${esc(tag)}</span>` : ''}
            ${badge ? `<span class="tag pdp-badge${badge.tone ? ' tag--gold' : ''}">${esc(badge.text)}</span>` : ''}
          </span>
          ${body ? `<span class="pdp-card__text">${esc(body)}</span>` : ''}
          ${meta ? `<span class="pdp-card__meta">${esc(meta)}</span>` : ''}
        </span>
      </label>`;
    }

    renderPhotos() {
      const P = this.rules.photo || {};
      const ph = this.cfg.photos || { min: 1, max: 1 };
      const status = {
        checking: ['Checking…', ''],
        great: ['Great', 'ok'],
        good: ['Good', 'ok'],
        review: ['Needs art review', 'warn'],
      };
      this.paint(this.$('[data-thumbs]'), this.s.photos.map((p, i) => {
        const [label, tone] = status[p.status] || status.checking;
        const dims = p.w ? `${p.w} × ${p.h} px` : p.note || '';
        return `<li class="pdp-thumb pdp-thumb--${p.status}">
          <span class="pdp-thumb__img">${p.previewable === false ? '<span class="pdp-thumb__noimg" aria-hidden="true">HEIC</span>' : `<img src="${p.url}" alt="">`}</span>
          <span class="pdp-thumb__meta">
            <span class="pdp-thumb__name">${esc(p.name)}</span>
            <span class="pdp-thumb__status pdp-thumb__status--${tone}">${esc(label)}</span>
            <span class="pdp-thumb__dims small muted">${esc(dims)}</span>
            ${p.upload === 'uploading' ? `<span class="pdp-thumb__progress" role="progressbar" aria-label="Uploading ${esc(p.name)}" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${p.progress || 0}" data-progress="${p.id}" style="--p:${p.progress || 0}%"></span><span class="small muted">Uploading securely…</span>` : ''}
            ${p.upload === 'done' ? '<span class="small muted">Uploaded privately ✓</span>' : ''}
            ${p.upload === 'failed' ? `<span class="pdp-thumb__why small">${esc(p.uploadError)} <button type="button" class="pdp-thumb__retry" data-retry="${i}">Retry</button></span>` : ''}
            ${p.status === 'review' ? `<span class="pdp-thumb__why small">${p.previewable === false ? 'We couldn\'t preview this file here.' : `This photo is under ${P.minLongEdgePx}px on its long edge.`} You can still order: an artist will check it and talk to you before anything is made.</span>` : ''}
          </span>
          <button type="button" class="pdp-thumb__remove" data-remove="${i}" aria-label="Remove ${esc(p.name)}">Remove</button>
        </li>`;
      }).join(''));
      const drop = this.$('[data-drop]');
      const full = this.s.photos.length >= ph.max;
      drop.classList.toggle('is-full', full);
      this.$('[data-file-input]').disabled = full;
      drop.querySelector('.pdp-drop__title').textContent = full
        ? (ph.max === 1 ? 'Photo added' : `All ${ph.max} photos added`)
        : this.s.photos.length ? 'Add another photo' : 'Add photo';

      // Live preview: first previewable photo inside the mat.
      const first = this.s.photos.find((p) => p.previewable !== false && p.url);
      this.$$('.pdp__art .art__image').forEach((box) => {
        let img = box.querySelector('img.pdp-user-photo');
        if (first) {
          if (!img) { img = document.createElement('img'); img.className = 'pdp-user-photo'; img.alt = 'Your photograph, illustrative placement preview'; box.appendChild(img); }
          if (img.getAttribute('src') !== first.url) img.src = first.url;
          box.classList.add('has-photo');
        } else if (img) { img.remove(); box.classList.remove('has-photo'); }
      });
    }

    renderInspiration() {
      const entry = this.s.inspiration;
      const preview = this.$('[data-inspiration-preview]');
      preview.hidden = !entry;
      if (!entry) return;
      const img = this.$('[data-inspiration-thumb]');
      img.hidden = entry.previewable === false;
      if (entry.previewable !== false && entry.url && img.getAttribute('src') !== entry.url) img.src = entry.url;
      const state = entry.upload === 'uploading' ? `Uploading ${entry.name}…`
        : entry.upload === 'failed' ? `${entry.name}: ${entry.uploadError}`
          : entry.upload === 'done' ? `${entry.name} · added privately` : entry.name;
      this.$('[data-inspiration-status]').textContent = state;
      this.$('[data-inspiration-retry]').hidden = entry.upload !== 'failed';
    }

    // ---------------------------------------------------------------- photos
    addFiles(fileList) {
      const P = this.rules.photo || {};
      const ph = this.cfg.photos || { min: 1, max: 1 };
      const msgs = [];
      for (const file of Array.from(fileList || [])) {
        if (this.s.photos.length >= ph.max) { msgs.push(`You can add up to ${ph.max} photo${ph.max > 1 ? 's' : ''}. Remove one to swap it.`); break; }
        const isHeic = /\.(heic|heif)$/i.test(file.name) || /heic|heif/i.test(file.type);
        if (!/^image\//.test(file.type) && !isHeic) { msgs.push(`${file.name} isn't an image we can use.`); continue; }
        if (file.size > P.maxMb * 1024 * 1024) { msgs.push(`${file.name} is over ${P.maxMb} MB. Please send a smaller copy.`); continue; }
        const entry = { id: rid('ph'), file, name: file.name, url: URL.createObjectURL(file), status: 'checking', w: 0, h: 0 };
        this.s.photos.push(entry);
        // Upload straight to the private dearingold-uploads bucket. Only the bundle id and a signed,
        // staff-only view link go on the order (private properties); file URLs never do.
        if (this.uploadEndpoint) this.uploadPhoto(entry);
        const img = new Image();
        img.onload = () => {
          entry.w = img.naturalWidth; entry.h = img.naturalHeight;
          const long = Math.max(entry.w, entry.h);
          entry.status = long < P.minLongEdgePx ? 'review' : long >= P.idealLongEdgePx ? 'great' : 'good';
          this.update();
        };
        img.onerror = () => { entry.status = 'review'; entry.previewable = false; entry.note = 'Preview not available'; this.update(); };
        img.src = entry.url;
      }
      this.$('[data-photo-msg]').textContent = msgs.join(' ');
      this.clearError('photos');
      this.update();
    }

    addInspiration(fileList) {
      const file = Array.from(fileList || [])[0];
      if (!file) return;
      if (!this.uploadEndpoint) { this.setError('inspiration', 'Image upload is unavailable right now. Please describe the look instead.'); return; }
      const P = this.rules.photo || {};
      const isHeic = /\.(heic|heif)$/i.test(file.name) || /heic|heif/i.test(file.type);
      if (!/^image\//.test(file.type) && !isHeic) { this.setError('inspiration', 'Choose a JPG, PNG, WEBP or HEIC image.'); return; }
      if (file.size > P.maxMb * 1024 * 1024) { this.setError('inspiration', `Choose an image under ${P.maxMb} MB.`); return; }
      this.removeInspiration();
      const entry = { id: rid('in'), kind: 'inspiration', file, name: file.name, url: URL.createObjectURL(file), previewable: !isHeic, upload: this.uploadEndpoint ? 'uploading' : 'local' };
      this.s.inspiration = entry;
      this.clearError('inspiration');
      this.$('[data-style-request]').open = true;
      if (this.uploadEndpoint) this.uploadPhoto(entry);
      this.update();
    }

    removeInspiration() {
      const entry = this.s.inspiration;
      if (!entry) return;
      this.s.inspiration = null;
      this.deleteRemoteFile(entry);
      if (entry.url) URL.revokeObjectURL(entry.url);
      const input = this.$('[data-inspiration-input]');
      if (input) input.value = '';
      this.clearError('inspiration');
      this.update();
    }

    removePhoto(i) {
      const p = this.s.photos[i];
      if (!p) return;
      try { URL.revokeObjectURL(p.url); } catch (_) { /* noop */ }
      this.deleteRemoteFile(p);
      this.s.photos.splice(i, 1);
      this.$('[data-photo-msg]').textContent = `${p.name} removed.`;
      this.update();
      const next = this.$(`[data-remove="${Math.min(i, this.s.photos.length - 1)}"]`) || this.$('[data-file-input]');
      if (next && !next.disabled) next.focus();
    }

    // ---------------------------------------------------------------- events
    bind() {
      this.form.addEventListener('submit', (e) => { e.preventDefault(); this.submit(); });
      this.addEventListener('click', (e) => {
        const r = e.target.closest('[data-retry]');
        if (r) { const entry = this.s.photos[Number(r.dataset.retry)]; if (entry) this.uploadPhoto(entry); }
        if (e.target.closest('[data-inspiration-retry]') && this.s.inspiration) this.uploadPhoto(this.s.inspiration);
        if (e.target.closest('[data-inspiration-remove]')) { e.preventDefault(); this.removeInspiration(); }
      });
      this.addEventListener('change', (e) => {
        const t = e.target;
        if (t.name === 'dg-treatment') this.s.treatment = t.value;
        else if (t.name === 'dg-format') { this.s.format = t.value; }
        else if (t.name === 'dg-size') this.s.size = t.value;
        else if (t.name === 'dg-frame') this.s.frame = t.value;
        else if (t.name === 'dg-gift') { t.checked ? this.s.gifts.add(t.value) : this.s.gifts.delete(t.value); }
        else if (t.matches('[data-file-input]')) { this.addFiles(t.files); t.value = ''; return; }
        else if (t.matches('[data-inspiration-input]')) { this.addInspiration(t.files); t.value = ''; return; }
        else if (t.matches('[data-proof-optin]')) this.s.proofOptIn = t.checked;
        else if (t.matches('[data-priority]')) this.s.priority = t.checked;
        else if (t.matches('[data-rights]')) { this.s.rights = t.checked; if (t.checked) this.clearError('rights'); }
        else if (t.matches('[data-needed-by]')) this.s.neededBy = t.value;
        else if (t.dataset.field) { this.readField(t); if (t.type === 'checkbox') this.renderTreatments(); }
        else return;
        this.update();
      });
      this.addEventListener('input', (e) => {
        const t = e.target;
        if (t.matches('[data-style-direction]')) { this.s.styleDirection = t.value; this.update(); }
        else if (t.dataset.field && t.type !== 'checkbox') { this.readField(t); this.updateTypeset(); this.updateCount(t); }
        else if (t.id === 'dg-gift-note') this.s.giftNote = t.value;
      });
      this.addEventListener('click', (e) => {
        const rm = e.target.closest('[data-remove]');
        if (rm) { e.preventDefault(); this.removePhoto(Number(rm.dataset.remove)); }
      });
      // drag & drop
      const drop = this.$('[data-drop]');
      ['dragenter', 'dragover'].forEach((ev) => drop.addEventListener(ev, (e) => { e.preventDefault(); drop.classList.add('is-over'); }));
      ['dragleave', 'drop'].forEach((ev) => drop.addEventListener(ev, (e) => { e.preventDefault(); drop.classList.remove('is-over'); }));
      drop.addEventListener('drop', (e) => { if (e.dataTransfer && e.dataTransfer.files) this.addFiles(e.dataTransfer.files); });

      // mobile sticky bar mirrors the primary button; hidden while the real one is on screen
      const bar = this.$('[data-bar]');
      this.$('[data-bar-add]').addEventListener('click', () => this.submit());
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((ents) => ents.forEach((en) => bar.classList.toggle('is-hidden', en.isIntersecting)));
        io.observe(this.$('.pdp-submit'));
      }
    }

    readField(el) {
      const k = el.dataset.field;
      this.s.values[k] = el.type === 'checkbox' ? el.checked : el.value;
      if (el.value || el.checked) this.clearError(k);
    }

    updateCount(el) {
      const c = this.$(`[data-count-for="${el.dataset.field}"]`);
      if (c) c.textContent = `${el.value.length} / ${el.maxLength}`;
    }

    updateTypeset() {
      const v = this.s.values;
      const name = v.names || v['pet-name'] || v['first-name'] || v['family-name'] || '';
      const d = parseISO(v.date || v['date-optional']);
      const bits = [name, d ? longDate(d) : '', v.years || '', v.place || '', v.caption || ''].map((x) => String(x).trim()).filter(Boolean);
      const box = this.$('[data-typeset]');
      box.hidden = !bits.length;
      this.$('[data-typeset-text]').textContent = bits.join(' · ');
    }

    update() {
      // treatment list may change with remembrance/proof conditions
      this.renderTreatments();
      this.renderSizeFrame();
      this.renderPhotos();
      this.renderInspiration();
      this.renderProof();
      this.renderEstimate();
      this.renderGifts();
      this.syncRadios();
      this.updateHeader();
      this.updatePreview();
      this.updateTypeset();
      this.updatePrice();
      this.updateTimeline();
    }

    syncRadios() {
      const set = (name, val) => this.$$(`input[name="${name}"]`).forEach((i) => { i.checked = i.value === val; });
      set('dg-format', this.s.format);
      set('dg-treatment', this.s.treatment);
    }

    updateHeader() {
      const st = this.story;
      this.$('[data-eyebrow]').textContent = (st && st.occasion) || this.primaryOccasion || 'Dearingold';
      const gentle = this.remembrance;
      this.$('[data-photos-title]').textContent = gentle ? '1. Add their photo' : '1. Add your photo';
      this.$('[data-photos-intro]').textContent = gentle
        ? 'Take your time. We review each photo and show you a proof before making a remembrance piece.'
        : 'A clear original works best. We will contact you if it needs review.';
      this.closest('.pdp')?.classList.toggle('pdp--gentle', gentle);
    }

    updatePreview() {
      this.$$('[data-art-for]').forEach((el) => { el.hidden = el.dataset.artFor !== this.s.format; });
      const style = this.rules.treatments[this.s.treatment] || {};
      const styleImage = this.cfg.avatar === 'pets' ? (style.petImage || style.image) : style.image;
      const hasPhoto = this.s.photos.some((p) => p.previewable !== false && p.url);
      this.$$('.pdp__art .art__image').forEach((box) => {
        let sample = box.querySelector('.pdp-style-example');
        if (styleImage) {
          if (!sample) { sample = document.createElement('img'); sample.className = 'pdp-style-example'; sample.alt = ''; box.appendChild(sample); }
          if (sample.getAttribute('src') !== styleImage) sample.src = styleImage;
          sample.hidden = hasPhoto;
        } else if (sample) sample.remove();
      });
      const material = (this.rules.formats[this.s.format] || {}).label || 'material';
      this.$('[data-stage-note]').textContent = hasPhoto
        ? `Your source photo is shown for layout only. The ${material} finish and chosen art style are made and reviewed after you order.`
        : `Illustrative art layout, not a physical ${material} sample. Your piece will be created from the photos you upload.`;
      const frame = String(this.s.frame || 'black').toLowerCase();
      this.$$('.pdp__art .art').forEach((fig) => {
        fig.classList.remove('art--black', 'art--natural', 'art--white', 'art--signature', 'art--bare');
        fig.classList.add(['black', 'natural', 'white', 'signature'].includes(frame) ? `art--${frame}` : 'art--bare');
      });
    }

    total() {
      const v = this.variant;
      if (!v) return null;
      let sum = v.price * this.quantity;
      const extras = [];
      if (this.s.priority && this.priorityAvailable) { const a = this.addon('priority-proof'); sum += a.price; extras.push(`${this.rules.proof.priority.label} ${money(a.price)}`); }
      for (const g of this.s.gifts) {
        const r = this.rules.gifts[g];
        if (r && r.addon && this.addon(r.addon)) { const a = this.addon(r.addon); sum += a.price; extras.push(`${r.label} ${money(a.price)}`); }
      }
      if (this.quantity > 1) extras.unshift(`2 copies`);
      return { sum, extras };
    }

    updatePrice() {
      const t = this.total();
      const v = this.variant;
      const txt = t ? money(t.sum) : 'Unavailable';
      this.$('[data-price]').textContent = txt;
      this.$('[data-add-price]').textContent = t ? txt : '';
      this.$('[data-bar-price]').textContent = txt;
      this.$('[data-price-note]').textContent = t && t.extras.length ? `Includes ${t.extras.join(', ')}` : '';
      this.$('[data-variant-input]').value = v ? v.id : '';
      const add = this.$('[data-add]');
      if (add.dataset.state !== 'adding') add.disabled = !v;
      const f = this.rules.formats[this.s.format];
      const framed = ['framed-print', 'gold-print', 'metallic-foil'].includes(this.s.format);
      this.$('[data-summary]').textContent = v ? [f && f.label, `${this.s.size.replace('×', ' × ')} in`, framed ? `${this.s.frame} frame` : this.s.frame].filter(Boolean).join(' · ') : 'This combination is not available.';
    }

    updateTimeline() {
      const section = this.closest('.pdp');
      if (!section) return;
      const tag = section.querySelector('[data-tl-proof-tag]');
      const txt = section.querySelector('[data-tl-proof-text]');
      tag.textContent = 'included for every piece';
      txt.textContent = 'You approve a watermarked proof before anything is made. One structured revision round is included.';
      section.querySelector('[data-tl-proof]').classList.remove('is-skipped');
      const route = (this.variant && this.variant.route) || (this.rules.formats[this.s.format] || {}).route;
      const made = {
        'art-print': 'Your artwork is printed on fine-art paper.',
        canvas: 'Your artwork is printed and stretched on canvas.',
        acrylic: 'Your artwork is printed and mounted on acrylic.',
        metal: 'Your artwork is printed on aluminium.',
        'foil-print': 'Your artwork is printed with real metallic foil.',
      };
      section.querySelector('[data-tl-made]').textContent = route === 'MOON-INHOUSE'
        ? 'Foiled by hand in the Dearingold studio.'
        : made[this.s.format] || 'Your artwork is professionally printed and framed.';
    }

    // ---------------------------------------------------------------- validation + cart
    setError(key, msg) {
      const el = this.$(`[data-error-for="${key}"]`);
      if (el) el.textContent = msg;
      if (key === 'inspiration') this.$('[data-style-request]').open = true;
      const input = key === 'photos' ? this.$('[data-file-input]') : key === 'inspiration' ? this.$('[data-inspiration-input]') : key === 'rights' ? this.$('[data-rights]') : this.$(`[data-field="${key}"]`);
      if (input) input.setAttribute('aria-invalid', 'true');
      el?.closest('.pdp-step')?.classList.add('has-error');
      return input || el;
    }
    clearError(key) {
      const el = this.$(`[data-error-for="${key}"]`);
      if (el) el.textContent = '';
      const input = key === 'photos' ? this.$('[data-file-input]') : key === 'inspiration' ? this.$('[data-inspiration-input]') : key === 'rights' ? this.$('[data-rights]') : this.$(`[data-field="${key}"]`);
      input?.removeAttribute('aria-invalid');
      const step = el?.closest('.pdp-step');
      if (step && !step.querySelector('.pdp-error:not(:empty)')) step.classList.remove('has-error');
    }

    validate() {
      const bad = [];
      const ph = this.cfg.photos || { min: 1, max: 1 };
      ['photos', 'rights', ...Object.keys(this.rules.fields)].forEach((k) => this.clearError(k));
      this.clearError('inspiration');
      if (this.s.photos.length < ph.min) bad.push(this.setError('photos', `Please add at least ${ph.min} photo${ph.min > 1 ? 's' : ''}.`));
      else if (this.uploadEndpoint && this.s.photos.some((p) => p.upload === 'uploading')) bad.push(this.setError('photos', 'Your photos are still uploading. This usually takes a few seconds.'));
      else if (this.uploadEndpoint && this.s.photos.some((p) => p.upload !== 'done')) bad.push(this.setError('photos', 'One of your photos didn’t upload. Retry it or remove it.'));
      if (this.s.inspiration && this.uploadEndpoint && this.s.inspiration.upload !== 'done') {
        bad.push(this.setError('inspiration', this.s.inspiration.upload === 'uploading' ? 'Your inspiration image is still uploading.' : 'Your inspiration image did not upload. Retry it or remove it.'));
      }
      for (const k of this.cfg.fields || []) {
        const f = this.rules.fields[k];
        if (!f || !this.fieldRequired(k, f)) continue;
        if (!String(this.s.values[k] || '').trim()) bad.push(this.setError(k, `Please add ${f.label.replace(/\s*\(.*\)\s*$/, '').toLowerCase()}.`));
      }
      if (!this.s.rights) bad.push(this.setError('rights', 'Please confirm you have the right to use these photographs.'));
      return bad.filter(Boolean);
    }

    properties() {
      const P = {};
      const set = (k, v) => { if (v !== undefined && v !== null && v !== false && String(v).trim() !== '') P[k] = String(v).trim(); };
      const st = this.story;
      const v = this.variant;
      const t = this.rules.treatments[this.s.treatment] || {};
      const f = this.rules.formats[this.s.format] || {};
      const vals = this.s.values;
      const proof = this.proof;
      const est = this.estimate();

      // --- public (customer-readable) ---
      set('Occasion', st ? st.occasion : this.primaryOccasion);
      set('Story', st ? st.label : this.s.story === 'other' ? this.s.storyOther || 'Something else' : '');
      set('Treatment', t.label);
      set('Style direction', this.s.styleDirection);
      if (this.s.inspiration) set('Style inspiration', 'Image attached');
      set('Finish', f.label);
      for (const k of this.cfg.fields || []) {
        const label = FIELD_PROP[k];
        if (!label || label === 'Art notes') continue;
        const raw = vals[k];
        set(label, (k === 'date' || k === 'date-optional') && parseISO(raw) ? longDate(parseISO(raw)) : raw);
      }
      const notes = [vals.arrangement, vals.notes, vals.cleanup ? 'Clean-up requested: remove cars, bins or people, or rebuild hidden details.' : ''].map((x) => String(x || '').trim()).filter(Boolean);
      set('Art notes', notes.join('\n\n'));
      set('Photos', `${this.s.photos.length} uploaded`);
      set('Proof', this.rules.proof[proof.level].label + (this.s.priority && this.priorityAvailable ? ` · ${this.rules.proof.priority.label}` : ''));
      if (parseISO(this.s.neededBy)) set('Needed by', longDate(parseISO(this.s.neededBy)));
      const gifts = [...this.s.gifts].filter((g) => g !== 'duplicate').map((g) => this.rules.gifts[g].label);
      set('Gift', gifts.join(', '));
      if (this.s.gifts.has('handwritten-note')) set('Gift note', this.s.giftNote);
      if (this.s.gifts.has('duplicate')) set('Second copy', 'Yes');

      // --- private (workflow; underscore keys are hidden from the customer at checkout) ---
      const flags = [];
      if (this.s.photos.some((p) => p.status === 'review')) flags.push('low-res');
      if (vals.cleanup) flags.push('cleanup-requested');
      if (this.isMultiPet()) flags.push('multi-pet');
      if (this.s.styleDirection.trim() || this.s.inspiration) flags.push('custom-style');
      if (COMPOSITE_T.includes(this.s.treatment)) flags.push('composite');
      if (RESTORATION_T.includes(this.s.treatment)) flags.push('restoration');
      if (this.remembrance) flags.push('remembrance');
      if (est.cls === 'date-sensitive') flags.push('date-tight');
      if (est.cls === 'not-available') flags.push('date-impossible');

      set('_dg_config_version', this.rules.version);
      set('_dg_upload_bundle_id', this.ids.bundle);
      if (this.remote) set('_dg_photos', this.remote.viewUrl); // signed staff-only gallery link
      if (this.s.inspiration?.remote) set('_dg_inspiration_file', this.s.inspiration.remote);
      set('_dg_artwork_request_id', this.ids.request);
      set('_dg_route_candidate', v && v.route);
      set('_dg_supplier_product_key', v && v.key);
      set('_dg_format_code', this.s.format);
      set('_dg_treatment_code', this.s.treatment);
      set('_dg_style_prompt_version', t.promptVersion);
      set('_dg_reference_subject', this.cfg.avatar === 'pets' ? 'pet' : 'general');
      set('_dg_style_reference_id', t.promptVersion ? `${t.promptVersion}-${this.cfg.avatar === 'pets' ? 'pet-sample-2026-09-23' : 'sample-2026-09-22'}` : '');
      set('_dg_proof_level', proof.level);
      set('_dg_delivery_class', est.cls);
      set('_dg_risk_flags', flags.join(','));
      set('_dg_rights_attestation_version', this.rules.attestations.rights.version);
      set('_dg_consent_version', this.rules.attestations.consent.version);
      return P;
    }

    items() {
      const v = this.variant;
      const items = [{ id: v.id, quantity: this.quantity, properties: this.properties() }];
      const parent = { For: this.data.title, _dg_parent: this.ids.request };
      if (this.s.priority && this.priorityAvailable) items.push({ id: this.addon('priority-proof').variantId, quantity: 1, properties: { ...parent } });
      for (const g of this.s.gifts) {
        const r = this.rules.gifts[g];
        if (r && r.addon && this.addon(r.addon)) items.push({ id: this.addon(r.addon).variantId, quantity: 1, properties: { ...parent } });
      }
      return items;
    }

    setButton(state) {
      const btn = this.$('[data-add]');
      const label = this.$('[data-add-label]');
      btn.dataset.state = state;
      btn.setAttribute('aria-busy', state === 'adding' ? 'true' : 'false');
      btn.disabled = state === 'adding' || !this.variant;
      const bar = this.$('[data-bar-add]');
      const text = { default: 'Add to cart', adding: 'Adding…', added: 'Added to cart', error: 'Try again' }[state];
      label.textContent = text;
      bar.textContent = text;
      bar.disabled = state === 'adding' || !this.variant;
    }

    async submit() {
      if (this._busy) return;
      const formErr = this.$('[data-form-error]');
      formErr.textContent = '';
      const bad = this.validate();
      if (bad.length || !this.variant) {
        formErr.textContent = !this.variant ? 'This size and presentation combination is not available.' : `Please check ${bad.length === 1 ? 'the highlighted step' : 'the highlighted steps'} above.`;
        const first = bad[0];
        if (first) {
          const target = first.closest('.pdp-step') || first;
          target.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'center' });
          setTimeout(() => first.focus({ preventScroll: true }), reducedMotion() ? 0 : 350);
        }
        return;
      }
      this._busy = true;
      this.setButton('adding');
      try {
        if (!window.DG || !DG.cart) throw new Error('Cart is not ready yet. Please reload the page.');
        await DG.cart.add(this.items());
        if (this.remote) this.remote.attached = true;
        this.setButton('added');
        this.$('[data-gocart]').hidden = false;
        DG.toast && DG.toast(`Added ${esc(this.data.title)} to your cart. <a href="/cart">View cart</a>`, 6000);
        this.newIds(); // the next piece is a new artwork request
        setTimeout(() => { if (this.$('[data-add]').dataset.state === 'added') this.setButton('default'); }, 2600);
      } catch (err) {
        console.error('[dg-configurator] add failed', err);
        this.setButton('error');
        formErr.textContent = `We couldn't add this to your cart. ${err && err.message ? err.message + '. ' : ''}Please try again.`;
      } finally {
        this._busy = false;
      }
    }
  }

  if (!customElements.get('dg-configurator')) customElements.define('dg-configurator', DGConfigurator);
})();
